import type { PolicyLink } from '../services/types'

/** Title-case a word (e.g. "TOYOTA" → "Toyota"). */
export function titleCase(s: string): string {
  const t = s.trim()
  return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : t
}

/** Build short "Year Make Model" from payload fields. */
export function shortVehicleFromPayload(
  yearBuilt: string | null | undefined,
  make: string | null | undefined,
  model: string | null | undefined
): string {
  const y = yearBuilt?.trim()
  const ma = make?.trim()
  const mo = model?.trim()
  if (!y && !ma && !mo) return ''
  const parts = [y || '', ma ? titleCase(ma) : '', mo ? titleCase(mo) : ''].filter(Boolean)
  return parts.join(' ')
}

/** Derive short "Year Make Model" from full vehicle string when payload fields missing. */
export function shortVehicleDisplay(full: string): string {
  const trimmed = full.trim()
  if (!trimmed) return trimmed
  const tokens = trimmed.split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return ''
  const take = tokens.slice(0, 3)
  const formatted = take.map((t, i) =>
    i === 0 && /^\d{4}$/.test(t) ? t : titleCase(t)
  )
  return formatted.join(' ')
}

function parseLooseCurrencyAmount(s: string): number | null {
  let t = s
    .replace(/[\u00a0\u202f]/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
  t = t.replace(/^aud\s*/i, '').replace(/^\$\s*/, '').replace(/,/g, '').replace(/\s/g, '')
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/** Sum insured from API/cache may be a number or a formatted string (e.g. "125,000"). */
function parseNumericLoose(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (typeof raw === 'string') return parseLooseCurrencyAmount(raw)
  return null
}

function formatAudDollars(n: number): string {
  return `$${Math.round(n).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`
}

export type PolicyVehicleValueRowParts = {
  /** Row heading: "Agreed value" or "Market value" (replaces generic vehicle value). */
  rowLabel: string
  /** Cell body: formatted `$X` when amount known; otherwise legacy fallbacks. */
  rowValue: string
}

type VehicleValuePolicyInput = Pick<PolicyLink, 'vehicleValue' | 'sumInsured' | 'marketOrAgreedValue'>

function computeVehicleValueRow(policy: VehicleValuePolicyInput): PolicyVehicleValueRowParts {
  const rawVv =
    policy.vehicleValue == null || policy.vehicleValue === ''
      ? ''
      : String(policy.vehicleValue).trim()
  const lower = rawVv.toLowerCase()

  const labelAgreed = lower === 'agreed value'
  const labelMarket = lower === 'market value'

  // Explicit text wins; else align with API default (Entsia/backend treats absent as MARKET, not AGREED)
  const isMarket =
    labelMarket ||
    (!labelAgreed && !labelMarket && policy.marketOrAgreedValue !== 'AGREED')

  const rowLabel = isMarket ? 'Market value' : 'Agreed value'

  // Market policies must never render their dollar figure, even if a stale payload still carries one.
  if (isMarket) {
    return { rowLabel, rowValue: '' }
  }

  const sumN = parseNumericLoose(policy.sumInsured)
  const vvN = rawVv ? parseLooseCurrencyAmount(rawVv) : null
  const sumValid = sumN != null && Number.isFinite(sumN)
  const vvValid = vvN != null && Number.isFinite(vvN)

  let amount: number | null = null
  if (sumValid) amount = sumN!
  else if (vvValid && !labelAgreed) amount = vvN!

  let rowValue: string
  if (amount != null && Number.isFinite(amount)) {
    rowValue = formatAudDollars(amount)
  } else if (labelAgreed) {
    rowValue = 'Agreed value'
  } else if (rawVv) {
    rowValue = rawVv
  } else {
    rowValue = '—'
  }

  return { rowLabel, rowValue }
}

/** Row label + value for Policy / Manage policy vehicle value row (label is agreed vs market). */
export function getPolicyVehicleValueRowParts(policy: VehicleValuePolicyInput): PolicyVehicleValueRowParts {
  return computeVehicleValueRow(policy)
}

/**
 * Value column only (dollar amount when known). Row title is {@link getPolicyVehicleValueRowParts}.`rowLabel` ("Agreed value" / "Market value").
 */
export function formatPolicyVehicleValueDisplay(policy: VehicleValuePolicyInput): string {
  return computeVehicleValueRow(policy).rowValue
}

/**
 * Header vehicle line — matches Policy tab (short year/make/model when available).
 * When there is no vehicle description, Entsia-style fallback `Policy ${policyRef}` is shown as-is
 * (not passed through shortVehicleDisplay, which would title-case "Policy" and the ref).
 */

export function getHeaderVehicleLine(policy: PolicyLink): string {
  const fallbackLine = `Policy ${policy.policyRef}`
  const vehicleName = policy.vehicleName || fallbackLine

  const fromPayload = shortVehicleFromPayload(policy.yearBuilt, policy.make, policy.model)
  if (fromPayload) return fromPayload

  if (vehicleName === fallbackLine) {
    return fallbackLine
  }

  return shortVehicleDisplay(vehicleName) || vehicleName
}

/** Whether the policy is paid once per term (Entsia `ANNUAL`, display `Annual`, or `Annually`). */
export function isAnnualPaymentFrequency(paymentFrequency: string | undefined): boolean {
  const freq = String(paymentFrequency ?? '').trim().toUpperCase()
  return freq === 'ANNUAL' || freq.startsWith('ANNUAL')
}

function formatPremiumAmount(value: number | string | undefined | null): string {
  const n = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(n)) return 'N/A'
  return `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Per-period payment row from Entsia instalmentAmount + payment frequency (API raw codes). */
export function instalmentRowFromPolicy(policy: PolicyLink): { name: string; value: string } | null {
  const raw = policy.instalmentAmount
  if (raw == null) return null
  if (String(raw).trim() === '') return null
  const amount = Number(raw)
  if (Number.isNaN(amount)) return null
  const freq = String(policy.paymentFrequency || '').trim().toUpperCase()
  const value = formatPremiumAmount(amount)
  if (freq === 'MONTHLY') return { name: 'Monthly payment', value }
  if (freq === 'QUARTERLY') return { name: 'Quarterly payment', value }
  return null
}

/** Row title for term total premium on Policy tab; Payment tab uses {@link paymentTabTermPremiumLabel}. */
export function policyTabTermPremiumLabel(paymentFrequency: string | undefined): string {
  return isAnnualPaymentFrequency(paymentFrequency)
    ? 'Annual premium for this term'
    : 'Premium for this term'
}

/** Row title for term total premium on Payment tab. */
export function paymentTabTermPremiumLabel(paymentFrequency: string | undefined): string {
  return isAnnualPaymentFrequency(paymentFrequency)
    ? 'Annual premium for this term'
    : 'Premium for this term'
}
